/**
 * runCounts — the CLIENT side of the ONE canonical reviewer-count derivation (KB-1566).
 *
 * The numbers are computed ONCE, server-side, at publish time (`publishRunToFirestore.cjs` →
 * `functions/scripts/camp-data/_shared/deriveRunCounts.cjs`) from the run's core data source (the
 * match_report + the signature-collapsed variations_needed) and persisted on the run doc as
 * `runDoc.derived`. Every reviewer surface reads them through {@link readRunCounts} — NO surface
 * computes its own count, and the client never fetches the 1.3 MB match_report artifact (which is
 * what produced "match data unavailable").
 *
 * This module is the client mirror of `deriveRunCounts.cjs`; the shape + `RUN_COUNTS_SCHEMA_VERSION`
 * are the contract between them. `readRunCounts` PREFERS the persisted `runDoc.derived`; for legacy
 * run docs (published before KB-1566, no `derived`) it recomputes the scalar counts from already-
 * persisted run-doc fields (`proposals.*`, `metrics.*`) — it does NOT re-derive from the artifact, so
 * `matchedByVariation` is empty in that path and the caller keeps its legacy artifact fallback.
 */

/** Must equal `RUN_COUNTS_SCHEMA_VERSION` in `deriveRunCounts.cjs`. The reader gates on an exact match. */
export const RUN_COUNTS_SCHEMA_VERSION = "camp-data.runCounts.v1";

// KB-1768 — link refs (`"<i>|<suffix>"` against runDoc.linkPrefixes) expand at this normalizer
// boundary so every downstream surface keeps receiving plain absolute URLs; raw legacy URLs pass
// through untouched. See links.ts (the mirror of runDocLinkTable.cjs).
import { makeLinkExpander, type LinkExpander } from "./links";

/** Per-reason per-session blocked counts (the SESSION unit — distinct from the collapsed DECISION counts). */
export interface RunCountsBlockedByReason {
  missingLocation: number;
  ambiguousLocation: number;
  missingVariation: number;
  ambiguousVariation: number;
}

/**
 * One catalogued-offer member row behind a defined-term metric (KB-1616 click-target). KB-1652 — enriched
 * to the 8-field offer-modal row (sourced from the same `deriveCoverage.offerEntry` as the coverage panel
 * via `buildCoverageOfferPartition`, so both surfaces show identical rows). Fields omitted when absent.
 */
export interface RunCountsOfferEntry {
  /** KB-1768 — the catalog offer's document id (the `session` link target). Absent on legacy docs. */
  docId?: string;
  variationKey?: string;
  startDateIso?: string;
  endDateIso?: string;
  displayTitle?: string;
  minAge?: number;
  maxAge?: number;
  descriptionPreview?: string;
  priceRegular?: number;
  priceTier2?: number;
  priceTier3?: number;
  /** KB-1739 — the offer's provider detail page. Reviewer click-through link on the modal row. */
  url?: string;
  /** KB-1739 — the offer's separate registration page. A distinct reviewer click-through link. */
  regUrl?: string;
}

/** One observed-session member row behind the "New, fit catalog" metric (KB-1616 click-target). */
export interface RunCountsSessionEntry {
  title: string;
  /** KB-1768 — a matched offer's document id when the row resolves to one (coverage-sourced matched
   * sessions); new-fit rows have no offer yet, so they never carry it (no dead session links). */
  docId?: string;
  /** KB-1739 — the provider detail/listing page. */
  url?: string;
  /** KB-1739 — the separate registration page (emitted distinctly; was coalesced into `url` before). */
  regUrl?: string;
  startDateIso?: string;
  /** KB-3718 — the observed (or preview) price `deriveRunCounts` persists, so its level chip has a price beside it. */
  priceRegular?: number;
  observedPriceLevel?: string | null;
  observedProgrammePrice?: Record<string, unknown>;
  observedPriceRefusal?: Record<string, unknown>;
}

/**
 * KB-3718 — the KB-3698 price provenance off a persisted member, key-for-key. The KEY is carried even when its
 * value is `null` ("none stated"), and omitted when absent ("not recorded"): the price-level chip reads that
 * difference, and these member normalizers are allowlists, so without this the level never reached the screen.
 * The TypeScript twin of `scripts/camp-data/_shared/priceProvenanceProjection.cjs`, pinned to it by
 * `campDataPriceLevel.parity.test.ts`.
 */
export function priceProvenanceKeys(o: Record<string, unknown>): Pick<RunCountsSessionEntry, "observedPriceLevel" | "observedProgrammePrice" | "observedPriceRefusal"> {
  if (!Object.prototype.hasOwnProperty.call(o, "observedPriceLevel")) return {};
  const level = o.observedPriceLevel;
  const obj = (v: unknown) => (v && typeof v === "object" && !Array.isArray(v) ? (v as Record<string, unknown>) : undefined);
  const programme = obj(o.observedProgrammePrice);
  const refusal = obj(o.observedPriceRefusal);
  return {
    observedPriceLevel: level === null ? null : typeof level === "string" ? level : String(level),
    ...(programme ? { observedProgrammePrice: programme } : {}),
    ...(refusal ? { observedPriceRefusal: refusal } : {}),
  };
}

/**
 * KB-1616 — capped member lists behind the DEFINED-TERM metrics (the click-targets). `matched` /
 * `catalogued` / `notSeen` are catalogued offers; `newFit` are the accept-to-create observed sessions.
 * Empty arrays on legacy / no-offer-source docs (those numbers render non-clickable).
 */
export interface RunCountsOfferGroups {
  matched: RunCountsOfferEntry[];
  newFit: RunCountsSessionEntry[];
  catalogued: RunCountsOfferEntry[];
  notSeen: RunCountsOfferEntry[];
}

/**
 * KB-3839 — one card per vendor product the org's product map did not bind (mirror of
 * `vendorProductMapLib.collapseUnboundVariationBindings`). `productKey` is "" for sessions that carry no
 * product id (withdrawn or never emitted); those are grouped per cause × location instead.
 */
export interface VariationBindingCard {
  cardKey: string;
  /** The first row's cause; `causes` holds every distinct one on the card. */
  cause: string;
  causes: string[];
  productKey: string;
  campLocationIds: string[];
  sessionCount: number;
  /**
   * Sessions the title heuristics could place in no variation at their location. Their create proposal is
   * kept; whether Program resolution lists a variation for them is the variationsNeeded collapse's call.
   */
  needsNewVariationSessions: number;
  /** Sessions whose shape an operator marked "Don't create": still withheld, proposing nothing. */
  createDeclinedSessions: number;
  /** What the title heuristics would have bound (a guess to check, never an answer). */
  heuristicVariationIds: string[];
  sampleTitles: string[];
  samplePageUrls: string[];
  mappedLocationIds: string[];
  mappedVariationKeys: string[];
}

/** The canonical reviewer-count object. Mirror of `deriveRunCounts.cjs`'s output. */
export interface RunCounts {
  schemaVersion: string;
  /** Total sessions observed from source ("Found N"); null when the doc carries no session total. */
  sessionsFound: number | null;
  /** Sessions matched 1:1 to an existing catalog object. */
  sessionsMatched: number;
  /** Sessions blocked on catalog (sum of the four per-reason counts — the SESSION unit, e.g. 16). */
  sessionsBlocked: number;
  blockedByReason: RunCountsBlockedByReason;
  /** Sessions the operator marked "Don't create" (SUPPRESSED_*). A benign census bucket (KB-1552). */
  sessionsSuppressed: number;
  /**
   * Residual census bucket (KB-1552): observed sessions dropped BEFORE classification — skipped non-camp
   * rows the matcher never emits a result row for (all-ages / recurring / long-span / non-specific-location
   * WebTrac listings; see `deriveRunCounts.cjs`). Makes "Found N" additive:
   * `sessionsMatched + sessionsBlocked + sessionsSuppressed + sessionsFiltered = sessionsFound`. `null` when
   * the doc carries no observed total to diff against (the residual is unknown, not 0).
   */
  sessionsFiltered: number | null;
  /** Catalog DECISIONS to create a new program (collapsed per location × keystone signature — e.g. 2, NOT 13). */
  programsToCreate: number;
  /** Catalog DECISIONS to disambiguate a program (collapsed per location × candidate-set — e.g. 1, NOT 3). */
  programsToDisambiguate: number;
  /** Catalog DECISIONS to create a new location (collapsed per unmatched venue). */
  locationsToCreate: number;
  /** Catalog DECISIONS to disambiguate a location (collapsed per location × candidate-set). */
  locationsToDisambiguate: number;
  /** Per existing variation: matched-session count, keyed `locationId|variationKey`. Empty on legacy docs. */
  matchedByVariation: Record<string, number>;
  /**
   * The PRECISE "New, fit catalog" / accept-to-create count (KB-1569): matched sessions with no current offer
   * (legacy reconcile plan.missing + adapter-native gated proposedNew). Equals the length of the Review & Accept
   * "New sessions to add" list. `null` on docs with no offer-layer source (caller falls back to the legacy
   * residual `observed − matched − blocked` for old docs).
   */
  newFitToCreate: number | null;
  /**
   * KB-1616 — the DEFINED-TERM "Matched": observed sessions matched 1:1 to an EXISTING catalog offer (the
   * offer-level reconcile), NOT the matchLib PROGRAM-fit `sessionsMatched` (which conflates Matched +
   * New-fit). This is ALSO the catalog-side "Match observed" — the same number from each end, so a large
   * Matched with 0 Match observed is impossible. `null` on docs with no offer-layer source (the caller keeps
   * the legacy single-"Matched" census + coverage-derived catalog side).
   */
  offerMatched?: number | null;
  /** KB-1616 — Catalogued: existing offers the run scanned. `offerMatched + catalogNotSeen` by construction. `null` w/o an offer source. */
  catalogued?: number | null;
  /** KB-1616 — Not seen this run: catalogued offers no observed session matched. `null` w/o an offer source. */
  catalogNotSeen?: number | null;
  /** KB-1616 — capped member lists behind Matched / New-fit / Catalogued / Not-seen (click-targets). `null` w/o an offer source. */
  offerGroups?: RunCountsOfferGroups | null;
  /**
   * KB-3839 — vendor products the org's product map withheld: ONE decision per product (a map entry), not
   * per session. Those sessions are already inside `blockedByReason.ambiguousVariation`. 0 / [] on docs
   * published before KB-3839 and for every org without a map.
   */
  productsToMap?: number;
  variationBindingsNeeded?: VariationBindingCard[];
  /** True when the persisted card list was capped, so a partial list never reads as the whole set. */
  variationBindingsNeededTruncated?: boolean;
}

function strList(v: unknown): string[] {
  return Array.isArray(v) ? v.map((x) => String(x ?? "")).filter(Boolean) : [];
}

/** Normalize the persisted KB-3839 per-product cards; malformed entries drop out. */
function normalizeVariationBindingCards(raw: unknown): VariationBindingCard[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((x) => x && typeof x === "object")
    .map((x) => {
      const o = x as Record<string, unknown>;
      const cause = String(o.cause ?? "");
      const causes = strList(o.causes);
      const card: VariationBindingCard = {
        cardKey: String(o.cardKey ?? ""),
        cause,
        causes: causes.length ? causes : cause ? [cause] : [],
        productKey: String(o.productKey ?? ""),
        campLocationIds: strList(o.campLocationIds),
        sessionCount: num(o.sessionCount),
        needsNewVariationSessions: num(o.needsNewVariationSessions),
        createDeclinedSessions: num(o.createDeclinedSessions),
        heuristicVariationIds: strList(o.heuristicVariationIds),
        sampleTitles: strList(o.sampleTitles),
        samplePageUrls: strList(o.samplePageUrls),
        mappedLocationIds: strList(o.mappedLocationIds),
        mappedVariationKeys: strList(o.mappedVariationKeys),
      };
      return card;
    })
    .filter((c) => c.cardKey);
}

/** The run-doc fields this reader consumes (loose — external Firestore JSON). */
export interface RunCountsRunDocLike {
  derived?: Partial<RunCounts> | null;
  /** KB-1647 — the canonical reviewer counts for the PAST partition (sessions already ended), for the audit accordion. */
  derivedPast?: Partial<RunCounts> | null;
  proposals?: {
    counts?: { location?: number; variation?: number } | null;
    variationsNeeded?: unknown[] | null;
  } | null;
  metrics?: {
    details?: { sessions?: number | null; matched?: number | null } | null;
    locRecon?: { proposals?: number | null } | null;
    progRecon?: { proposals?: number | null } | null;
  } | null;
  /** KB-1676 — the whole-run "WHAT THIS RUN CHANGED" breakdown (read by `readOfferUpdates`). */
  offerUpdates?: unknown;
  /** KB-1689 — the per-field CONTENT-parity scorecard (read by `readContentParity`). */
  contentParity?: unknown;
  /** KB-1768 — the per-run URL prefix table compressed `url`/`regUrl` refs resolve against. */
  linkPrefixes?: unknown;
}

function num(v: unknown): number {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function numOrNull(v: unknown): number | null {
  if (v == null) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

/** True when the run doc carries the server-computed canonical counts (post-KB-1566 / backfilled). */
export function hasDerivedCounts(runDoc: RunCountsRunDocLike | null | undefined): boolean {
  return String(runDoc?.derived?.schemaVersion ?? "") === RUN_COUNTS_SCHEMA_VERSION;
}

/**
 * The residual census bucket: persisted `sessionsFiltered` when present (post-KB-1552 docs), else
 * recomputed from the other census fields so OLD docs (pre-KB-1552 `derived`, or the legacy fallback)
 * still add up immediately without a re-run (KB-1552 AC4). `null` when there's no observed total.
 */
function residualFiltered(
  persisted: unknown,
  found: number | null,
  matched: number,
  blocked: number,
  suppressed: number,
): number | null {
  if (persisted != null) {
    const n = Number(persisted);
    if (Number.isFinite(n)) return Math.max(0, n);
  }
  if (found == null) return null;
  return Math.max(0, found - matched - blocked - suppressed);
}

/** A finite number, or undefined — so a missing age/price reads as absent rather than 0. */
function numOrUndefined(v: unknown): number | undefined {
  if (v == null) return undefined;
  const n = Number(v);
  return Number.isFinite(n) ? n : undefined;
}

/**
 * Normalize a persisted offer-entry member list (catalogued / matched / not-seen). KB-1652 — reads the
 * enriched 8-field row; each field stays absent when the persisted entry omits it (legacy docs carry only
 * variationKey/startDateIso/displayTitle and still render — the new fields just read undefined).
 */
function normalizeOfferEntries(raw: unknown, expandLink: LinkExpander): RunCountsOfferEntry[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((x) => x && typeof x === "object")
    .map((x) => {
      const o = x as Record<string, unknown>;
      const out: RunCountsOfferEntry = {};
      // KB-1768 — the offer's docId (the `session` link target); absent on legacy docs.
      if (o.docId != null && String(o.docId)) out.docId = String(o.docId);
      if (o.variationKey != null) out.variationKey = String(o.variationKey);
      if (o.startDateIso != null) out.startDateIso = String(o.startDateIso);
      if (o.endDateIso != null) out.endDateIso = String(o.endDateIso);
      if (o.displayTitle != null) out.displayTitle = String(o.displayTitle);
      const minAge = numOrUndefined(o.minAge);
      const maxAge = numOrUndefined(o.maxAge);
      if (minAge !== undefined) out.minAge = minAge;
      if (maxAge !== undefined) out.maxAge = maxAge;
      if (o.descriptionPreview != null) out.descriptionPreview = String(o.descriptionPreview);
      const priceRegular = numOrUndefined(o.priceRegular);
      const priceTier2 = numOrUndefined(o.priceTier2);
      const priceTier3 = numOrUndefined(o.priceTier3);
      if (priceRegular !== undefined) out.priceRegular = priceRegular;
      if (priceTier2 !== undefined) out.priceTier2 = priceTier2;
      if (priceTier3 !== undefined) out.priceTier3 = priceTier3;
      // KB-1739/KB-1768 — the two reviewer click-through links: raw legacy URLs pass through, compressed
      // refs expand against runDoc.linkPrefixes; unresolvable ⇒ omitted (no dead link).
      const url = expandLink(o.url);
      const regUrl = expandLink(o.regUrl);
      if (url !== undefined) out.url = url;
      if (regUrl !== undefined) out.regUrl = regUrl;
      return out;
    });
}

/** Normalize a persisted session member list (the "New, fit catalog" accept-to-create rows). */
function normalizeSessionEntries(raw: unknown, expandLink: LinkExpander): RunCountsSessionEntry[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((x) => x && typeof x === "object")
    .map((x) => {
      const o = x as Record<string, unknown>;
      const out: RunCountsSessionEntry = { title: o.title != null ? String(o.title) : "(untitled session)" };
      // KB-1768 — a matched offer's docId when the row resolves to one; new-fit rows never carry it.
      if (o.docId != null && String(o.docId)) out.docId = String(o.docId);
      // KB-1739/KB-1768 — expand compressed refs; raw legacy URLs pass through.
      const url = expandLink(o.url);
      const regUrl = expandLink(o.regUrl);
      if (url !== undefined) out.url = url;
      if (regUrl !== undefined) out.regUrl = regUrl;
      if (o.startDateIso != null) out.startDateIso = String(o.startDateIso);
      // KB-3718 round 3, finding 1 — the price rides with its level: a level chip beside no price cannot be
      // checked against anything. `deriveRunCounts.sessionEntryFromOfferRow` persists it; this allowlist
      // dropped it.
      const priceRegular = numOrNull(o.priceRegular);
      if (priceRegular != null) out.priceRegular = priceRegular;
      return { ...out, ...priceProvenanceKeys(o) };
    });
}

/** Normalize the persisted offer-group member lists (KB-1616); null when the doc carries no offer source. */
function normalizeOfferGroups(raw: unknown, expandLink: LinkExpander): RunCountsOfferGroups | null {
  if (!raw || typeof raw !== "object") return null;
  const o = raw as Record<string, unknown>;
  return {
    matched: normalizeOfferEntries(o.matched, expandLink),
    newFit: normalizeSessionEntries(o.newFit, expandLink),
    catalogued: normalizeOfferEntries(o.catalogued, expandLink),
    notSeen: normalizeOfferEntries(o.notSeen, expandLink),
  };
}

/** Normalize a persisted `derived` object defensively (fill any missing field, coerce types). */
function normalize(d: Partial<RunCounts>, expandLink: LinkExpander): RunCounts {
  const r = (d.blockedByReason ?? {}) as Partial<RunCountsBlockedByReason>;
  const matched = d.matchedByVariation && typeof d.matchedByVariation === "object" ? d.matchedByVariation : {};
  const sessionsFound = numOrNull(d.sessionsFound);
  const sessionsMatched = num(d.sessionsMatched);
  const sessionsBlocked = num(d.sessionsBlocked);
  const sessionsSuppressed = num(d.sessionsSuppressed);
  return {
    schemaVersion: RUN_COUNTS_SCHEMA_VERSION,
    sessionsFound,
    sessionsMatched,
    sessionsBlocked,
    sessionsSuppressed,
    sessionsFiltered: residualFiltered(d.sessionsFiltered, sessionsFound, sessionsMatched, sessionsBlocked, sessionsSuppressed),
    blockedByReason: {
      missingLocation: num(r.missingLocation),
      ambiguousLocation: num(r.ambiguousLocation),
      missingVariation: num(r.missingVariation),
      ambiguousVariation: num(r.ambiguousVariation),
    },
    programsToCreate: num(d.programsToCreate),
    programsToDisambiguate: num(d.programsToDisambiguate),
    locationsToCreate: num(d.locationsToCreate),
    locationsToDisambiguate: num(d.locationsToDisambiguate),
    matchedByVariation: Object.fromEntries(Object.entries(matched).map(([k, v]) => [k, num(v)])),
    newFitToCreate: numOrNull(d.newFitToCreate),
    // KB-1616 — the offer-level reconcile partition. numOrNull preserves the "no offer source" signal
    // (null) on pre-KB-1616 docs so the UI keeps the legacy single-"Matched" census + coverage catalog side.
    offerMatched: numOrNull(d.offerMatched),
    catalogued: numOrNull(d.catalogued),
    catalogNotSeen: numOrNull(d.catalogNotSeen),
    offerGroups: normalizeOfferGroups((d as Record<string, unknown>).offerGroups, expandLink),
    productsToMap: num(d.productsToMap),
    variationBindingsNeeded: normalizeVariationBindingCards(d.variationBindingsNeeded),
    variationBindingsNeededTruncated: d.variationBindingsNeededTruncated === true,
  };
}

/**
 * Legacy fallback: recompute the SCALAR counts from already-persisted run-doc fields for docs published
 * before KB-1566 (no `derived`). NOT a re-derivation from the artifact — `matchedByVariation` is empty
 * (the caller keeps its artifact fallback for old docs) and the collapsed disambiguate/location decision
 * counts are unknown (0) without the per-session signatures. `programsToCreate` still collapses when the
 * doc carries the KB-1535 `variationsNeeded`, degrading to the per-session count only for pre-1535 docs.
 */
function fromLegacyRunDoc(runDoc: RunCountsRunDocLike): RunCounts {
  const counts = runDoc.proposals?.counts ?? {};
  const details = runDoc.metrics?.details ?? {};
  const locProposals = num(runDoc.metrics?.locRecon?.proposals);
  const progProposals = num(runDoc.metrics?.progRecon?.proposals);
  const locFallback = num(counts.location);
  const varFallback = num(counts.variation);
  const blockedLoc = runDoc.metrics?.locRecon ? locProposals : locFallback;
  const blockedVar = runDoc.metrics?.progRecon ? progProposals : varFallback;

  const variationsNeeded = runDoc.proposals?.variationsNeeded;
  const programsToCreate = Array.isArray(variationsNeeded) ? variationsNeeded.length : varFallback;

  const sessionsFound = numOrNull(details.sessions);
  const sessionsMatched = num(details.matched);
  const sessionsBlocked = blockedLoc + blockedVar;

  return {
    schemaVersion: RUN_COUNTS_SCHEMA_VERSION,
    sessionsFound,
    sessionsMatched,
    sessionsBlocked,
    // Legacy docs carry no SUPPRESSED tally on `metrics.details`; treat as 0 and route the whole
    // observed − matched − blocked remainder into the residual `sessionsFiltered` so old runs (raleigh:
    // 719 − 647 − 16 = 56) add up immediately without a re-run (KB-1552 AC4).
    sessionsSuppressed: 0,
    sessionsFiltered: residualFiltered(undefined, sessionsFound, sessionsMatched, sessionsBlocked, 0),
    blockedByReason: {
      // Legacy docs carry only the aggregated `proposals.counts` (location = missing+ambiguous), so the
      // per-reason split isn't recoverable; surface the aggregate under the dominant reason, 0 elsewhere.
      missingLocation: locFallback,
      ambiguousLocation: 0,
      missingVariation: varFallback,
      ambiguousVariation: 0,
    },
    programsToCreate,
    programsToDisambiguate: 0,
    locationsToCreate: 0,
    locationsToDisambiguate: 0,
    matchedByVariation: {},
    // Legacy docs carry no offer-layer source on the doc — the precise count is unknown; the caller falls
    // back to the residual. (KB-1569)
    newFitToCreate: null,
    // KB-1616 — pre-derived docs carry no offer-level partition; null keeps the legacy single-"Matched"
    // census + the coverage-derived catalog side (no re-run needed for old docs to keep rendering).
    offerMatched: null,
    catalogued: null,
    catalogNotSeen: null,
    offerGroups: null,
    productsToMap: 0,
    variationBindingsNeeded: [],
    variationBindingsNeededTruncated: false,
  };
}

/**
 * Read the canonical reviewer counts for a run doc. The SINGLE entry point every reviewer surface uses.
 * Returns the server-persisted `runDoc.derived` when present (the normal path — all post-KB-1566 docs),
 * else the legacy scalar fallback. Never throws; an absent/garbage doc yields all-zero counts.
 */
export function readRunCounts(runDoc: RunCountsRunDocLike | null | undefined): RunCounts {
  if (!runDoc) return fromLegacyRunDoc({});
  if (hasDerivedCounts(runDoc)) return normalize(runDoc.derived as Partial<RunCounts>, makeLinkExpander(runDoc.linkPrefixes));
  return fromLegacyRunDoc(runDoc);
}

/**
 * KB-1647 — true when the run doc carries the server-computed canonical counts for the PAST partition.
 * Absent on legacy docs and on runs with no past sessions.
 */
export function hasDerivedCountsPast(runDoc: RunCountsRunDocLike | null | undefined): boolean {
  return String(runDoc?.derivedPast?.schemaVersion ?? "") === RUN_COUNTS_SCHEMA_VERSION;
}

/**
 * KB-1647 — read the canonical reviewer counts for the PAST partition (the "Past sessions" audit accordion).
 * Returns the server-persisted `runDoc.derivedPast` when present, else `null` (legacy docs, or runs with no
 * past sessions) — the caller omits the accordion in that case. There is NO legacy scalar fallback: past data
 * is new with KB-1647, so a doc that lacks `derivedPast` simply has no past partition to show.
 */
export function readRunCountsPast(runDoc: RunCountsRunDocLike | null | undefined): RunCounts | null {
  if (!hasDerivedCountsPast(runDoc)) return null;
  return normalize(runDoc!.derivedPast as Partial<RunCounts>, makeLinkExpander(runDoc!.linkPrefixes));
}

// ---------------------------------------------------------------------------
// KB-1676 — the "WHAT THIS RUN CHANGED" per-category breakdown (`runDoc.offerUpdates`).
// ---------------------------------------------------------------------------

/** The breakdown categories a changed offer field maps to (KB-1685 added `normalization`). */
export type OfferUpdateCategory = "availability" | "pricing" | "regStatus" | "normalization" | "other";

/** One field change on an updated offer — the drill-down field/from/to row. */
export interface OfferFieldChange {
  field: string;
  /** JSON-safe normalized before value (string | number | null — the writer flattened Timestamps/refs). */
  from: unknown;
  to: unknown;
  category: OfferUpdateCategory;
  /** true = auto-written this run (availability/reg-status); false = frozen, FLAGGED for review (pricing/other). */
  applied: boolean;
  /** KB-1760 — true when a reviewer APPROVED this flagged change onto the offer (the callable stamped
   *  `appliedByReviewer` on the run-doc member change). Persisted, so it survives a reload and is visible on
   *  EVERY surface — the reload-safe, cross-surface parallel to a surface's in-session "approved this session"
   *  set. Absent (false) on pre-KB-1760 docs and on changes not yet approved. */
  appliedByReviewer?: boolean;
}

/** One changed session/offer in the breakdown: identity + preview (for the drill-down row) + its field changes. */
export interface OfferUpdateMember {
  /** KB-1731 — the existing offer's document id, so the reviewer can approve a flagged (`applied:false`) change
   * onto it via the KB-1730 `applyCampDataOfferFieldChanges` callable (which targets writes by `offerDocId`).
   * Absent on pre-KB-1731 run docs (persisted before the deriver carried it) ⇒ those flagged rows stay read-only. */
  docId?: string;
  displayTitle: string;
  variationKey: string;
  startDateIso: string | null;
  endDateIso: string | null;
  minAge: number | null;
  maxAge: number | null;
  priceRegular: number | null;
  priceTier2: number | null;
  priceTier3: number | null;
  descriptionPreview: string;
  /** KB-1739 — the offer's provider detail page (reviewer click-through). Absent when the offer carried none. */
  url?: string;
  /** KB-1739 — the offer's separate registration page. A distinct reviewer click-through link. */
  regUrl?: string;
  changes: OfferFieldChange[];
  /** KB-1685 — true when the new session title shares ~zero meaningful words with the existing offer's title
   * (likely a cross-program content-key match). Renders a "suspect match" tag in the drill-down. */
  suspectMatch: boolean;
  /** KB-3718 — where the price was stated; see `priceProvenanceKeys`. */
  observedPriceLevel?: string | null;
  observedProgrammePrice?: Record<string, unknown>;
  observedPriceRefusal?: Record<string, unknown>;
}

/**
 * The whole-run per-category change breakdown persisted at `runDoc.offerUpdates` (KB-1676). `updated` = distinct
 * changed offers; the four sub-categories count the offers with ≥1 change in each (overlapping — an offer with
 * both an availability flip and a price change is in `availability` AND `pricing`, so the sub-counts can sum to
 * more than `updated`). `members` is capped server-side.
 */
export interface OfferUpdates {
  updated: number;
  availability: number;
  pricing: number;
  regStatus: number;
  /** KB-1685 — offers whose change was expected normalization (override-nulling + snapshot echoes); EXCLUDED
   * from pricing/other so the real changes aren't drowned. Overlaps with the others like the rest. */
  normalization: number;
  other: number;
  members: OfferUpdateMember[];
}

const OFFER_UPDATE_CATEGORY_SET: ReadonlySet<string> = new Set([
  "availability",
  "pricing",
  "regStatus",
  "normalization",
  "other",
]);

function normalizeOfferFieldChange(x: unknown): OfferFieldChange | null {
  if (!x || typeof x !== "object") return null;
  const o = x as Record<string, unknown>;
  const field = o.field != null ? String(o.field) : "";
  if (!field) return null;
  const category = OFFER_UPDATE_CATEGORY_SET.has(String(o.category)) ? (String(o.category) as OfferUpdateCategory) : "other";
  return {
    field,
    from: o.from === undefined ? null : o.from,
    to: o.to === undefined ? null : o.to,
    category,
    applied: o.applied === true,
    // KB-1760 — the persisted reviewer-approve stamp is an object `{ uid, appliedAt }`; the client only needs its
    // presence. Coerce to a boolean so every count derivation treats it uniformly (and legacy docs read false).
    appliedByReviewer: o.appliedByReviewer != null && o.appliedByReviewer !== false,
  };
}

function normalizeOfferUpdateMember(x: unknown, expandLink: LinkExpander): OfferUpdateMember | null {
  if (!x || typeof x !== "object") return null;
  const o = x as Record<string, unknown>;
  // KB-1739/KB-1768 — expand compressed link refs; raw legacy URLs pass through.
  const url = expandLink(o.url);
  const regUrl = expandLink(o.regUrl);
  const changes = Array.isArray(o.changes)
    ? o.changes.map(normalizeOfferFieldChange).filter((c): c is OfferFieldChange => c != null)
    : [];
  return {
    // KB-1731 — carry the offer docId through only when present (forward-only; absent ⇒ member.docId undefined ⇒
    // the reviewer keeps that member's flagged rows read-only, no approve target).
    ...(o.docId != null && String(o.docId) ? { docId: String(o.docId) } : {}),
    displayTitle: o.displayTitle != null ? String(o.displayTitle) : "",
    variationKey: o.variationKey != null ? String(o.variationKey) : "",
    startDateIso: o.startDateIso != null ? String(o.startDateIso) : null,
    endDateIso: o.endDateIso != null ? String(o.endDateIso) : null,
    minAge: numOrNull(o.minAge),
    maxAge: numOrNull(o.maxAge),
    priceRegular: numOrNull(o.priceRegular),
    priceTier2: numOrNull(o.priceTier2),
    priceTier3: numOrNull(o.priceTier3),
    descriptionPreview: o.descriptionPreview != null ? String(o.descriptionPreview) : "",
    // KB-1739 — the two reviewer click-through links; omitted when the persisted row carried none.
    ...(url !== undefined ? { url } : {}),
    ...(regUrl !== undefined ? { regUrl } : {}),
    changes,
    suspectMatch: o.suspectMatch === true,
    ...priceProvenanceKeys(o),
  };
}

/**
 * KB-1676 — read the whole-run "WHAT THIS RUN CHANGED" breakdown (`runDoc.offerUpdates`). Returns null when the
 * run carries no breakdown (legacy docs / no change / no offer-write step, or a present-but-empty field) — the
 * caller then keeps the plain "updated N" chip. Never throws; defensively normalizes external Firestore JSON.
 */
export function readOfferUpdates(runDoc: RunCountsRunDocLike | null | undefined): OfferUpdates | null {
  const raw = runDoc?.offerUpdates;
  if (!raw || typeof raw !== "object") return null;
  const o = raw as Record<string, unknown>;
  const expandLink = makeLinkExpander(runDoc?.linkPrefixes);
  const members = Array.isArray(o.members)
    ? o.members.map((m) => normalizeOfferUpdateMember(m, expandLink)).filter((m): m is OfferUpdateMember => m != null)
    : [];
  const updated = num(o.updated);
  if (updated <= 0 && members.length === 0) return null;
  return {
    updated,
    availability: num(o.availability),
    pricing: num(o.pricing),
    regStatus: num(o.regStatus),
    normalization: num(o.normalization),
    other: num(o.other),
    members,
  };
}

/**
 * KB-1760 — a flagged offerUpdates change is RESOLVED (no longer needs review) when the writer auto-applied it
 * (`applied`) OR a reviewer approved it (persisted `appliedByReviewer`). The reviewer surface ORs its own
 * just-approved-this-session set on top for instant feedback before the run-doc snapshot round-trips.
 */
export function isOfferChangeResolved(c: OfferFieldChange): boolean {
  return c.applied === true || c.appliedByReviewer === true;
}

/**
 * KB-1760 — the REMAINING (still-flagged) offer count for a flagged category tile (Pricing / Other), derived
 * from the run doc so it is consistent across surfaces AND across a full reload. It is the persisted publish-time
 * category count MINUS the members whose EVERY change in that category is now resolved (auto-applied or
 * reviewer-approved). Cap-safe: the persisted scalar counts ALL offers incl. those beyond the 100-member cap,
 * and only members present in the list can be approved, so subtracting the fully-resolved present members yields
 * the true remaining. Clamped ≥0 so a repeat approve can never drive it negative (idempotent — AC3).
 *
 * `extraResolved(docId, field)` lets a reviewer surface fold in its in-session appliedKeys before the snapshot
 * lands; omit it for the pure reload-derived count.
 */
export function remainingFlaggedOffers(
  updates: OfferUpdates,
  cat: "pricing" | "other",
  extraResolved?: (docId: string, field: string) => boolean,
): number {
  let resolvedMembers = 0;
  for (const m of updates.members) {
    const catChanges = m.changes.filter((c) => c.category === cat);
    if (catChanges.length === 0) continue;
    const allResolved = catChanges.every(
      (c) => isOfferChangeResolved(c) || (!!m.docId && !!extraResolved && extraResolved(m.docId, c.field)),
    );
    if (allResolved) resolvedMembers += 1;
  }
  const persisted = cat === "pricing" ? updates.pricing : updates.other;
  return Math.max(0, persisted - resolvedMembers);
}

/**
 * KB-1731 / KB-1777 — the offer payload fields a flagged offerUpdates change can be approved onto its offer via
 * the KB-1730 `applyCampDataOfferFieldChanges` callable. MUST mirror `APPLIABLE_OFFER_FIELDS` in
 * functions/src/camp-data/applyOfferFieldChangesCore.ts (kept in sync by comment, same as CONTENT_PARITY_FIELD_KEYS).
 * Two families: the KB-1730 DIRECT content-parity fields, and (KB-1777) the EXPECTED-NORMALIZATION override /
 * snapshot fields, so the Run-details Normalization takeover gets the same "Approve all changes (N)" bar as the
 * flagged categories. Still NOT appliable (the callable refuses each `field-not-appliable`): `startDate`/`endDate`
 * (identity moves) and `locationData` (its audit `to` flattens a `providedByOrg` DocumentReference → not faithfully
 * writable). `offerUpdates` change fields are raw offer payload field names (the writer's `diffOfferFields` output),
 * so this checks the same field-name space the callable's audit does.
 */
export const APPLIABLE_OFFER_FIELDS = [
  // KB-1730 — direct content-parity payload fields.
  "description",
  "displayTitle",
  "url",
  "regUrl",
  "eligibilityNotes",
  "regEvents",
  "priceRegular",
  "priceTier2Calc",
  "priceTier3Calc",
  "numDaysCalc",
  // KB-2877 (review round 2, B3) — mirrors the callable's list. `numDaysCalc` is meaningless to
  // apply without its two coupled fields: `numDaysState` carries WHY the count is null (without it
  // the next publish rebuilds the offer at the variation default and undoes the repair), and
  // `pricePerDayCalc` must be nulled with it or a stale per-day figure keeps answering the
  // per-day filter beside a null count (data-model §1c/§1d).
  "numDaysState",
  "pricePerDayCalc",
  // KB-1777 — expected-normalization fields (override resets + variation/tier snapshots; locationData excluded).
  "numDaysOverride",
  "priceOverride",
  "priceNoteOverride",
  "pricingTierPriceOverrides",
  "agesAreOverrides",
  "variationData",
  // KB-3247 — the `orgName` LEAF of the still-excluded `locationData` compound. The bare compound
  // remains absent (its audit `to` flattens the `providedByOrg` ref); only this one real dotted path
  // is appliable.
  //
  // ⚠️ KNOWN REMAINING DRIFT, deliberately not fixed here: this mirror is ALSO missing the engine's
  // `variationData.timings.afterCareWindow` (KB-2815), which was added to
  // applyOfferFieldChangesCore.ts and never to this copy — so a care-window change still reads as
  // non-appliable in Run-details while the callable would in fact apply it. That is KB-2815's
  // defect, it changes client behaviour for a different feature, and it wants its own ticket rather
  // than a silent ride-along in a repair PR. It is the third mirror of this list and the only one
  // with NO parity guard at all ("kept in sync by comment", above) — which is exactly how it drifted.
  "locationData.orgName",
  // KB-4552 — KB-4535's stated drop-off/pick-up window compound field (the engine's SESSION_WINDOW_FIELD).
  "variationData.timings.dropOffPickUpWindow",
] as const;

const APPLIABLE_OFFER_FIELD_SET: ReadonlySet<string> = new Set(APPLIABLE_OFFER_FIELDS);

/** True when a flagged offerUpdates change on `field` can be approved onto its offer via the KB-1730 callable. */
export function isAppliableOfferField(field: string): boolean {
  return APPLIABLE_OFFER_FIELD_SET.has(String(field || "").trim());
}

// ---------------------------------------------------------------------------
// KB-1689 — the per-field CONTENT-parity scorecard (`runDoc.contentParity`), the KB-1533
// create-gate metric: adapter emission vs the curated (frozen-golden) catalog, derived
// server-side at publish from the observed→offers writer audit (deriveContentParity.cjs).
// ---------------------------------------------------------------------------

/** Scorecard field keys in display order — mirrors deriveContentParity's CONTENT_PARITY_FIELD_KEYS. */
export const CONTENT_PARITY_FIELD_KEYS = [
  "description",
  "displayTitle",
  "url",
  "regUrl",
  "eligibilityNotes",
  "regEvents",
  "priceRegular",
  "priceTier2",
  "priceTier3",
  "ages",
  "dates",
  "numDays",
] as const;

export type ContentParityFieldKey = (typeof CONTENT_PARITY_FIELD_KEYS)[number];

/** One underlying change behind a parity divergence/addition (drill-down row). */
export interface ContentParityChange {
  /** The parity field the change counts against (e.g. `priceTier2`, `ages`, `dates`). */
  parityField: string;
  /** The underlying payload field (e.g. `priceTier2Calc`, `variationData`, `startDate`). */
  field: string;
  /** KB-1720 — `"added"` (curated-empty → observed-value enrichment) vs `"diverged"` (real gap/content loss). */
  kind: "added" | "diverged";
  from: unknown;
  to: unknown;
  /** KB-1760 — true when a reviewer APPROVED this change onto the offer (the callable stamped `appliedByReviewer`
   *  on the run-doc member change). Persisted → reload-safe + cross-surface. Absent (false) on pre-KB-1760 docs. */
  appliedByReviewer?: boolean;
}

/** One matched offer with ≥1 content-field divergence (drill-down member; capped server-side). */
export interface ContentParityMember {
  docId: string;
  displayTitle: string;
  variationKey: string;
  startDateIso: string | null;
  /** KB-1739 — the offer's provider detail page. Reviewer click-through on the from/to row. */
  url?: string;
  /** KB-1739 — the offer's separate registration page. A distinct reviewer click-through. */
  regUrl?: string;
  /** KB-1685 — likely cross-program content-key match; its "divergences" may be a wrong pairing, not bad extraction. */
  suspectMatch: boolean;
  /** The parity fields this offer diverged on. */
  fields: string[];
  /** KB-1720 — the parity fields this offer was ENRICHED on (curated-empty → observed-value). */
  addedFields: string[];
  changes: ContentParityChange[];
}

/**
 * Per-field parity: `diverged` matched offers had a real gap on the field; `added` matched offers were
 * ENRICHED (curated-empty → observed-value, KB-1720); `parityPct` = (matched−diverged)/matched — additions
 * never reduce it.
 */
export interface ContentParityFieldStat {
  diverged: number;
  added: number;
  parityPct: number;
}

export interface ContentParity {
  /** The denominator: distinct existing offers the run consumed (matched 1:1 to the curated catalog). */
  matchedOffers: number;
  /** The create-gate scalar: the WORST per-field parity (the KB-1533 flip criterion reads this, ≥95%). */
  minFieldParityPct: number;
  fields: Record<string, ContentParityFieldStat>;
  members: ContentParityMember[];
}

function normalizeContentParityMember(x: unknown, expandLink: LinkExpander): ContentParityMember | null {
  if (!x || typeof x !== "object") return null;
  const o = x as Record<string, unknown>;
  // KB-1739/KB-1768 — expand compressed link refs; raw legacy URLs pass through.
  const url = expandLink(o.url);
  const regUrl = expandLink(o.regUrl);
  const changes: ContentParityChange[] = Array.isArray(o.changes)
    ? o.changes
        .map((c): ContentParityChange | null => {
          if (!c || typeof c !== "object") return null;
          const cc = c as Record<string, unknown>;
          const parityField = cc.parityField != null ? String(cc.parityField) : "";
          const field = cc.field != null ? String(cc.field) : "";
          if (!parityField && !field) return null;
          return {
            parityField: parityField || field,
            field: field || parityField,
            // KB-1720 — legacy v1 changes have no `kind`; default to "diverged" (their historical meaning).
            kind: cc.kind === "added" ? "added" : "diverged",
            from: cc.from === undefined ? null : cc.from,
            to: cc.to === undefined ? null : cc.to,
            // KB-1760 — persisted reviewer-approve stamp (object `{ uid, appliedAt }`); coerce to presence boolean.
            appliedByReviewer: cc.appliedByReviewer != null && cc.appliedByReviewer !== false,
          };
        })
        .filter((c): c is ContentParityChange => c != null)
    : [];
  return {
    docId: o.docId != null ? String(o.docId) : "",
    displayTitle: o.displayTitle != null ? String(o.displayTitle) : "",
    variationKey: o.variationKey != null ? String(o.variationKey) : "",
    startDateIso: o.startDateIso != null ? String(o.startDateIso) : null,
    // KB-1739 — the two reviewer click-through links; omitted when the persisted row carried none.
    ...(url !== undefined ? { url } : {}),
    ...(regUrl !== undefined ? { regUrl } : {}),
    suspectMatch: o.suspectMatch === true,
    fields: Array.isArray(o.fields) ? o.fields.map((f) => String(f)) : [],
    addedFields: Array.isArray(o.addedFields) ? o.addedFields.map((f) => String(f)) : [],
    changes,
  };
}

/**
 * KB-1689 — read the content-parity scorecard (`runDoc.contentParity`). Returns null when the run
 * carries none (legacy docs / no matched offers / pre-write publish) — the caller then omits the
 * scorecard section. Never throws; defensively normalizes external Firestore JSON.
 */
export function readContentParity(runDoc: RunCountsRunDocLike | null | undefined): ContentParity | null {
  const raw = runDoc?.contentParity;
  if (!raw || typeof raw !== "object") return null;
  const o = raw as Record<string, unknown>;
  const matchedOffers = num(o.matchedOffers);
  if (matchedOffers <= 0) return null;
  const rawFields = o.fields && typeof o.fields === "object" ? (o.fields as Record<string, unknown>) : {};
  const fields: Record<string, ContentParityFieldStat> = {};
  for (const [k, v] of Object.entries(rawFields)) {
    if (!v || typeof v !== "object") continue;
    const s = v as Record<string, unknown>;
    // KB-1720 — old v1 docs have no `added`; num(undefined) → 0 (defensive normalization).
    fields[k] = { diverged: num(s.diverged), added: num(s.added), parityPct: num(s.parityPct) };
  }
  const expandLink = makeLinkExpander(runDoc?.linkPrefixes);
  const members = Array.isArray(o.members)
    ? o.members.map((m) => normalizeContentParityMember(m, expandLink)).filter((m): m is ContentParityMember => m != null)
    : [];
  const minPersisted = numOrNull(o.minFieldParityPct);
  const minComputed = Object.values(fields).reduce((min, s) => Math.min(min, s.parityPct), 100);
  return {
    matchedOffers,
    minFieldParityPct: minPersisted != null ? minPersisted : minComputed,
    fields,
    members,
  };
}

/**
 * KB-1760 — the count of matched offers that DIVERGED on a content-parity field and whose divergence a reviewer
 * has since APPROVED (persisted `appliedByReviewer`), derived from the run doc so it survives a reload and is
 * cross-surface. The scorecard tile shows remaining diverged = `fields[field].diverged − contentParityAppliedCount`.
 * Only members diverged on the field are counted (`member.fields` gates it; a diverged member has no enrichment
 * change on the same field, so the persisted stamp on that field's change is unambiguously the divergence approve).
 *
 * `extraResolved(docId)` folds a surface's in-session applied set for instant feedback before the snapshot lands.
 */
export function contentParityAppliedCount(
  parity: ContentParity,
  field: string,
  extraResolved?: (docId: string) => boolean,
): number {
  let n = 0;
  for (const m of parity.members) {
    if (!m.fields.includes(field)) continue;
    const resolved =
      (!!extraResolved && extraResolved(m.docId)) ||
      m.changes.some((c) => c.parityField === field && c.kind === "diverged" && c.appliedByReviewer === true);
    if (resolved) n += 1;
  }
  return n;
}

/** One non-zero state in the additive "Found N" session census. */
export interface SessionCensusBucket {
  /** Stable key (also the React list key / test id suffix). */
  key: string;
  /** Operator-facing label. */
  label: string;
  count: number;
}

/**
 * The additive "Found N" session census (KB-1552 AC3/AC5). Every observed session lands in exactly one
 * bucket and the NON-ZERO buckets sum to `sessionsFound` — matched, the four per-reason blockers (SESSION
 * counts, NEVER the signature-collapsed `programsTo*` DECISION counts), operator-suppressed, and the
 * pre-match-skip residual. Zero states are omitted, so the reviewer renders only what actually happened.
 *
 * Invariant (asserted in tests): when `sessionsFound != null`, `sum(buckets) === sessionsFound`.
 */
export function sessionCensusBuckets(rc: RunCounts): SessionCensusBucket[] {
  const out: SessionCensusBucket[] = [];
  const push = (key: string, label: string, count: number) => {
    if (count > 0) out.push({ key, label, count });
  };
  const r = rc.blockedByReason;
  // KB-1616 — when the run carries the offer-level partition, "Matched" is the DEFINED-TERM offer-match
  // (e.g. 24), and "New, fit catalog" (the accept-to-create set, e.g. 629) is its OWN census bucket — NOT
  // folded into a single matchLib PROGRAM-fit "Matched" (653) with New-fit relegated to a sub-note. The two
  // replace the single `sessionsMatched` bucket; `offerMatched + newFit + blockers + suppressed + filtered`
  // sums to Observed by construction (the deriver's filtered residual absorbs the split). Legacy / no-offer
  // docs (`offerMatched == null`) keep the single matchLib "Matched" bucket — byte-identical to pre-KB-1616.
  if (rc.offerMatched != null) {
    push("matched", "Matched", rc.offerMatched);
    push("new-fit-catalog", "New, fit catalog", rc.newFitToCreate ?? 0);
  } else {
    push("matched", "Matched", rc.sessionsMatched);
  }
  push("missing-variation", "Missing variation", r.missingVariation);
  push("ambiguous-variation", "Ambiguous variation", r.ambiguousVariation);
  push("missing-location", "Missing location", r.missingLocation);
  push("ambiguous-location", "Ambiguous location", r.ambiguousLocation);
  // Legacy docs carry the aggregate `sessionsBlocked` (e.g. 16) but not the per-reason split (all 0 — it
  // isn't recoverable without the artifact). Surface the un-split remainder as a single "Blocked on catalog"
  // bucket so the census still sums to Found N for old runs without a re-run (KB-1552 AC4).
  const blockedKnown = r.missingVariation + r.ambiguousVariation + r.missingLocation + r.ambiguousLocation;
  push("blocked", "Blocked on catalog", Math.max(0, rc.sessionsBlocked - blockedKnown));
  push("suppressed", "Suppressed", rc.sessionsSuppressed);
  push("filtered", "Filtered (non-camp)", rc.sessionsFiltered ?? 0);
  return out;
}
